/**
 * `useInterruptedVisitRecovery`
 *
 * State machine + orchestration for the "Interrupted visit found" modal that
 * gates the AI-scribe auto-start when the call page opens.
 *
 * Flow
 *  - The call page calls `requestJoin(encounterId)` instead of joining Zoom
 *    directly. This probes `GET /scribe/sessions/:encId/active`.
 *  - When the probe finds a recoverable session for THIS encounter
 *    (`session.encounterId === encounterId` && `chunkCount > 0`) AND it isn't
 *    a session this tab itself just started, we open the modal and HOLD the
 *    join. Otherwise we fall straight through to `onProceedFresh(encounterId)`.
 *  - The modal's three actions resolve through `resume()`, `finish()`, and
 *    `discard()`.
 *
 * Cross-patient / cross-encounter isolation
 *  - We never adopt/resume/finish/discard a recovered session unless its
 *    `encounterId` matches the encounter the doctor is opening. The BE
 *    validates this too; the FE must not bypass it.
 *
 * Self-started guard
 *  - `markSelfStarted(sessionId)` records ids this tab minted so a fresh
 *    session we just created can't trip the recovery modal on a re-probe.
 *
 * The hook is deliberately UI-agnostic: the consuming page owns the Zoom
 * rejoin (`onResume`), the fresh-join (`onProceedFresh`), and the
 * finalize+navigate flow (`onFinish`). This hook only sequences them and owns
 * the modal's open/busy/degrade state.
 */
import { useCallback, useRef, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { confirmDiscardScribeSession } from './confirmDiscardScribeSession'
import type { JoinSessionResult } from '@/components/session'
import {
  useActiveScribeSession,
  type ActiveScribeSession,
  type ScribeChunk,
} from './useActiveScribeSession'

/** Which recovery action (if any) is currently in flight — drives spinners + disables. */
export type InterruptedVisitBusy = null | 'resume' | 'finish' | 'discard'

export interface InterruptedVisitRecoveryCallbacks {
  /**
   * Rejoin Zoom + adopt the recovered scribe session and render the prior
   * chunks. Returns the discriminated {@link JoinSessionResult} — only
   * `'failed'` triggers the modal's "call already ended" degrade state.
   * `'busy'` (a re-entrant no-op, e.g. double-click) is ignored, and
   * `'joined'` closes the modal.
   */
  onResume: (
    encounterId: string,
    session: ActiveScribeSession,
    chunks: ScribeChunk[],
  ) => Promise<JoinSessionResult>
  /**
   * Finalize the recovered session as a partial visit + generate the draft +
   * route to the progress-note draft. Does NOT join Zoom.
   */
  onFinish: (encounterId: string, session: ActiveScribeSession) => Promise<void>
  /** Proceed with a normal fresh start (no recovery / after discard). */
  onProceedFresh: (encounterId: string) => void
  /**
   * Leave the call shell when there's no longer a path forward — specifically
   * after a discard from the DEGRADE path (the call already ended, so there's
   * no live room to start fresh into). The consumer typically navigates back
   * to the patient chart so the doctor doesn't land on an empty call shell
   * with no call, no transcript, and no next action.
   */
  onExit: (encounterId: string) => void
}

export interface UseInterruptedVisitRecoveryResult {
  /**
   * Probe + gate entry point. Replaces a direct join call on the page.
   *
   * `opts.recoveryOnly` — when set and the probe finds NO recoverable session,
   * do nothing instead of falling through to a fresh join. Used by the `/call`
   * on-mount self-recovery probe so a plain refresh of an encounter with no
   * live session never auto-starts a brand-new call.
   */
  requestJoin: (encounterId: string, opts?: { recoveryOnly?: boolean }) => Promise<void>
  /** Record a session id this tab just minted (so it can't trip the modal). */
  markSelfStarted: (sessionId: string | null | undefined) => void
  /** Modal props. */
  modalOpen: boolean
  recoveredSession: ActiveScribeSession | null
  callEnded: boolean
  busy: InterruptedVisitBusy
  resume: () => void
  finish: () => void
  discard: () => void
  closeModal: () => void
}

export function useInterruptedVisitRecovery(
  callbacks: InterruptedVisitRecoveryCallbacks,
): UseInterruptedVisitRecoveryResult {
  const { getActiveSession, getChunks, discardSession } = useActiveScribeSession()

  // Hold the callbacks in a ref so the stable `requestJoin` / action handlers
  // always see the latest closures without being recreated each render.
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  const [modalOpen, setModalOpen] = useState(false)
  const [recoveredSession, setRecoveredSession] = useState<ActiveScribeSession | null>(null)
  const [callEnded, setCallEnded] = useState(false)
  const [busy, setBusy] = useState<InterruptedVisitBusy>(null)

  // The encounter the modal is currently gating + ids this tab itself started.
  const pendingEncounterIdRef = useRef<string | null>(null)
  const selfStartedIdsRef = useRef<Set<string>>(new Set())
  // Re-entrancy guard for `requestJoin` — a double-click on a join trigger, or
  // an autoJoin effect racing a manual virtual-visit click, can otherwise
  // double-probe `/active` and double-open the modal. Held true for the
  // duration of a probe.
  const requestingRef = useRef(false)

  const markSelfStarted = useCallback((sessionId: string | null | undefined) => {
    if (sessionId) selfStartedIdsRef.current.add(sessionId)
  }, [])

  const resetModal = useCallback(() => {
    setModalOpen(false)
    setRecoveredSession(null)
    setCallEnded(false)
    setBusy(null)
    pendingEncounterIdRef.current = null
  }, [])

  const requestJoin = useCallback<UseInterruptedVisitRecoveryResult['requestJoin']>(
    async (encounterId, opts) => {
      // Re-entrancy guard: ignore a second probe while one is already in
      // flight (double-click, or autoJoin + manual click racing). Without this
      // the modal can double-open and the `/active` probe fires twice.
      if (requestingRef.current) return
      requestingRef.current = true
      try {
        const session = await getActiveSession(encounterId)
        // Recovery gate: only intercept when there's a live session, with
        // captured lines, bound to THIS encounter, that this tab didn't start.
        const recoverable =
          !!session &&
          session.status === 'live' &&
          session.chunkCount > 0 &&
          session.encounterId === encounterId &&
          !selfStartedIdsRef.current.has(session.sessionId)
        if (!recoverable || !session) {
          // `recoveryOnly` (the /call on-mount self-recovery probe): stay idle
          // when nothing's recoverable instead of auto-starting a fresh call.
          if (opts?.recoveryOnly) return
          callbacksRef.current.onProceedFresh(encounterId)
          return
        }
        pendingEncounterIdRef.current = encounterId
        setRecoveredSession(session)
        setCallEnded(false)
        setBusy(null)
        setModalOpen(true)
      } finally {
        requestingRef.current = false
      }
    },
    [getActiveSession],
  )

  const resume = useCallback(() => {
    const encounterId = pendingEncounterIdRef.current
    const session = recoveredSession
    if (!encounterId || !session) return
    // Isolation double-check (BE validates too; never bypass on the FE).
    if (session.encounterId !== encounterId) {
      logError(
        'scribe-recovery',
        'resume blocked: session/encounter mismatch',
        new Error(`session.encounterId=${session.encounterId} encounterId=${encounterId}`),
      )
      return
    }
    setBusy('resume')
    void (async () => {
      try {
        let chunks: ScribeChunk[] = []
        try {
          chunks = await getChunks(encounterId, session.sessionId)
        } catch (err) {
          // Non-fatal — resume without the prior lines rather than dead-ending.
          logError('scribe-recovery', 'getChunks failed during resume', err)
        }
        const result = await callbacksRef.current.onResume(encounterId, session, chunks)
        if (result === 'joined') {
          resetModal()
        } else if (result === 'busy') {
          // A join was already in flight (e.g. a double-click, or autoJoin +
          // a manual Resume racing). This is NOT a dead room — clear the busy
          // spinner and leave the modal open so the doctor can retry Resume
          // once the in-flight join settles. Do NOT degrade to Finish/Discard.
          setBusy(null)
        } else {
          // result === 'failed' — the Zoom rejoin errored (dead room, signature
          // fetch failure, SDK rejection). Degrade to the "call already ended"
          // state so the doctor can Finish / Discard instead of dead-ending.
          // 'already-joined' lands here too and wants the same treatment: resume
          // claims with `takeover`, so it only appears when the claim call itself
          // failed open — and Finish / Discard is still the honest way out.
          setBusy(null)
          setCallEnded(true)
        }
      } catch (err) {
        logError('scribe-recovery', 'resume failed', err)
        setBusy(null)
        setCallEnded(true)
      }
    })()
  }, [recoveredSession, getChunks, resetModal])

  const finish = useCallback(() => {
    const encounterId = pendingEncounterIdRef.current
    const session = recoveredSession
    if (!encounterId || !session) return
    if (session.encounterId !== encounterId) {
      logError(
        'scribe-recovery',
        'finish blocked: session/encounter mismatch',
        new Error(`session.encounterId=${session.encounterId} encounterId=${encounterId}`),
      )
      return
    }
    setBusy('finish')
    void (async () => {
      try {
        await callbacksRef.current.onFinish(encounterId, session)
        // `onFinish` navigates to the draft on success; clear the modal.
        resetModal()
      } catch (err) {
        // `useFinalizeScribe` already surfaced its own notification.
        logError('scribe-recovery', 'finish failed', err)
        setBusy(null)
      }
    })()
  }, [recoveredSession, resetModal])

  const discard = useCallback(() => {
    const encounterId = pendingEncounterIdRef.current
    const session = recoveredSession
    if (!encounterId || !session) return
    if (session.encounterId !== encounterId) {
      logError(
        'scribe-recovery',
        'discard blocked: session/encounter mismatch',
        new Error(`session.encounterId=${session.encounterId} encounterId=${encounterId}`),
      )
      return
    }
    const lines = session.chunkCount
    confirmDiscardScribeSession({
      lineCount: lines,
      onConfirm: () => {
        setBusy('discard')
        void (async () => {
          try {
            await discardSession(encounterId, session.sessionId)
          } catch (err) {
            // Best-effort — even if the DELETE failed, fall through to a fresh
            // start so the doctor isn't trapped. Surface a gentle note.
            logError('scribe-recovery', 'discard failed', err)
            showNotification({
              color: 'yellow',
              message: 'Could not delete the previous transcript, starting a new session anyway.',
            })
          }
          const wasEnded = callEnded
          resetModal()
          if (!wasEnded) {
            // Normal (not call-ended) path: proceed to a fresh start in the
            // same live room.
            callbacksRef.current.onProceedFresh(encounterId)
          } else {
            // Degrade path: the call already ended, so there's no live room to
            // start fresh into. Closing the modal here would dead-end on an
            // empty call shell (no call, no transcript, no way forward), so
            // hand off to `onExit` to navigate somewhere sensible (the patient
            // chart).
            callbacksRef.current.onExit(encounterId)
          }
        })()
      },
    })
  }, [recoveredSession, discardSession, callEnded, resetModal])

  return {
    requestJoin,
    markSelfStarted,
    modalOpen,
    recoveredSession,
    callEnded,
    busy,
    resume,
    finish,
    discard,
    closeModal: resetModal,
  }
}
